import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import { Layers, LayersOutlined } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { manageKeyboardEvents } from "shared/common/utils/keyboardEvents/manageKeyboardEvents";
import PopoverBox from "shared/common/utils/popover/PopoverBox";
import LayerContent from "./LayerContent";

interface props {
  authMode: "view" | "edit";
}
export default function Layer({ authMode }: props) {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const layerConfiguration = defaultStore((state) => state.layerConfiguration);

  const getUpdatedImagePopover = sidebarStore(
    (state) => state.getUpdatedImagePopover
  );
  const changeTopicPopover = sidebarStore((state) => state.changeTopicPopover);
  const topicPopover = sidebarStore((state) => state.topicPopover);
  const changeSelectedTableRows = sidebarStore(
    (state) => state.changeSelectedTableRows
  );
  const changeSelectTools = sidebarStore((state) => state.changeSelectTools);
  const activeAddLayersListItem = sidebarStore(
    (state) => state.activeAddLayersListItem
  );
  const expandAddLayersList = sidebarStore(
    (state) => state.expandAddLayersList
  );
  const closeAllToolPopover = sidebarStore(
    (state) => state.closeAllToolPopover
  );
  const changeHoveredAxis = sidebarStore((state) => state.changeHoveredAxis);
  const changeSelectedAxis = sidebarStore((state) => state.changeSelectedAxis);
  const changeAxisControlPopover = sidebarStore(
    (state) => state.changeAxisControlPopover
  );

  //change topic button and popover
  const handleClickTopic = () => {
    closeAllToolPopover();
    changeTopicPopover(!topicPopover);
  };

  //add select feature option object and functions
  const picklayers: mm.Layer[] = [];

  const tooloptions = {
    selectedcbf: (selected_feature: mm.Feature) => {
      if (!viewer) return;
      const layer = viewer
        .getScene()
        .getLayerHandler()
        .getLayerByFeatureCollection(selected_feature.parentcollection);

      layer.featureselector.clearSelection();

      closeAllToolPopover();

      if (
        layer.type === mm.LAYERTYPE.NETWORKLAYER &&
        layer instanceof mm.NetworkLayer
      ) {
        const axisCollection = defaultStore.getState().axisCollection;
        if (axisCollection === undefined) return;
        const matchedAxis = axisCollection.find((axis: mm.AxisObject) =>
          axis.axis?.axis?.features.some(
            (feature: mm.Feature) => feature.id === selected_feature.id
          )
        );
        if (!matchedAxis) return;
        layer.addAxisToSelection(matchedAxis.axis);
        changeSelectedAxis(matchedAxis.axis);
        4;
        viewer
          .getScene()
          .getLRHandler()
          .setActiveMeasurementAxis(matchedAxis.axis);
        changeAxisControlPopover(true);

        viewer
          .getScene()
          .getLayerHandler()
          .getActiveLayer()
          ?.featureselector.clearSelection();
        viewer.getScene().getLayerHandler().setActiveLayer(null);
        changeSelectedTableRows(undefined);
      } else {
        layer.featureselector.addToSelection(selected_feature.id);

        viewer.getScene().getLayerHandler().setActiveLayer(layer);
        const geojson = selected_feature.toGeoJSON();
        changeSelectedTableRows(geojson);
        changeTopicPopover(!getUpdatedImagePopover());
      }
    },
    deselectedcbf: (deselected_feature: mm.Feature) => {
      if (!viewer) return;

      const layer = viewer
        .getScene()
        .getLayerHandler()
        .getLayerByFeatureCollection(deselected_feature.parentcollection);

      if (
        layer.type === mm.LAYERTYPE.NETWORKLAYER &&
        layer instanceof mm.NetworkLayer
      ) {
        const axisCollection = defaultStore.getState().axisCollection;
        if (axisCollection === undefined) return;
        const matchedAxis = axisCollection.find((axis: mm.AxisObject) =>
          axis.axis?.axis?.features.some(
            (feature: mm.Feature) => feature.id === deselected_feature.id
          )
        );
        if (!matchedAxis) return;
        layer.removeAxisFromSelection(matchedAxis.axis);
        changeSelectedAxis(null);
        viewer.getScene().getLRHandler().setActiveMeasurementAxis(null);
      } else {
        layer.featureselector.removeFromSelection(deselected_feature.id);
        viewer.getScene().getLayerHandler().setActiveLayer(null);
        changeSelectedTableRows(undefined);
      }
    },
    clearcbf: () => {
      for (let i = 0; i < picklayers.length; i++) {
        picklayers[i].featureselector.clearSelection();
      }
    },
    onovercbf: (feature_hovered: mm.Feature) => {
      if (!viewer) return;
      const layer = viewer
        .getScene()
        .getLayerHandler()
        .getLayerByFeatureCollection(feature_hovered.parentcollection);

      if (
        layer.type === mm.LAYERTYPE.NETWORKLAYER &&
        layer instanceof mm.NetworkLayer
      ) {
        const axisCollection = defaultStore.getState().axisCollection;
        if (axisCollection === undefined) return;
        const axis = axisCollection.find((axis) =>
          axis.axis?.axis?.features.some(
            (feature: mm.Feature) => feature.id === feature_hovered.id
          )
        );
        if (!axis) return;
        layer?.axisHighlightOn(axis.axis);
        changeHoveredAxis(axis.axis);
      } else {
        feature_hovered.highlightOn();
      }
    },
    onoutcbf: (feature_out: mm.Feature) => {
      if (!viewer) return;
      const layer = viewer
        .getScene()
        .getLayerHandler()
        .getLayerByFeatureCollection(feature_out.parentcollection);

      if (
        layer.type === mm.LAYERTYPE.NETWORKLAYER &&
        layer instanceof mm.NetworkLayer
      ) {
        const axisCollection = defaultStore.getState().axisCollection;
        if (axisCollection === undefined) return;
        changeHoveredAxis(null);
        const axis = axisCollection.find((axis) =>
          axis.axis?.axis?.features.some(
            (feature: mm.Feature) => feature.id === feature_out.id
          )
        );
        if (!axis) return;
        if (axis.axis === sidebarStore.getState().selectedAxis) return; // do not remove selection if the axis is selected
        layer?.axisHighlightOff(axis.axis);
      } else {
        if (sidebarStore.getState().selectedTableRows?.id === feature_out.id)
          return; // do not remove selection if the feature is selected
        feature_out.highlightOff();
      }
    },
    enablecbf: () => {
      const axisPickTool = sidebarStore.getState().axisPickTool;
      // if (axisPickTool && axisPickTool.enabled) axisPickTool.disable();
    },
    disablecbf: () => {
      const axisPickTool = sidebarStore.getState().axisPickTool;
      // if (axisPickTool && !axisPickTool.enabled) axisPickTool.enable();
    },
    hightlightonhover: false,
    activecursor: "pointer",
    clearonnullpick: true,
    singleactive: true,
    selectionmodel: mm.SELECTIONMODEL.SIMPLE,
  };

  //create select tool option when viewer is defined
  useEffect(() => {
    if (viewer !== undefined) {
      changeSelectTools(
        viewer
          .getScene()
          .getToolHandler()
          .createSelectFeatureTool(tooloptions, "select")
      );
    }
  }, [viewer]);

  //manage keyboard events while writing on inputs
  useEffect(() => {
    if (expandAddLayersList && topicPopover && activeAddLayersListItem !== "") {
      manageKeyboardEvents(viewer, true);
    } else {
      manageKeyboardEvents(viewer, false);
    }
  }, [activeAddLayersListItem, expandAddLayersList, topicPopover]);

  return (
    <>
      <Tooltip
        title={t("tooltip.layer")}
        placement="right"
        enterDelay={400}
        classes={{
          tooltip: "infra3d-buttonDescription",
        }}
      >
        <IconButton
          sx={{
            width: "40px",
            mx: "auto",
            color: "white",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
          classes={{
            root: topicPopover
              ? "infra3d-iconButtonRippleActive"
              : "infra3d-iconButtonRipple",
          }}
          onClick={() =>
            monitor(apm, "change-status-layertree | Toolbar", handleClickTopic)
          }
          disableRipple={true}
        >
          {!topicPopover && <LayersOutlined />}
          {topicPopover && <Layers />}
        </IconButton>
      </Tooltip>
      <PopoverBox visible={topicPopover}>
        {viewer !== undefined && layerConfiguration !== undefined && (
          <LayerContent authMode={authMode} />
        )}
      </PopoverBox>
    </>
  );
}
